import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  DialogTitle,
  Typography,
  Avatar,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  EditOutlined as EditIcon,
} from '@mui/icons-material';
import LenderOnboardingForm from './LenderOnboardingForm';
import OrganizationVerification from './OrganizationVerification';

const LenderOnboardingPopup = ({ open, onClose, initialData }) => {
  const [showVerification, setShowVerification] = useState(false);
  const [verificationData, setVerificationData] = useState({
    lenderId: null,
    userId: null,
    formData: null
  });
  const theme = useTheme();

  const isSuperAdmin = useMemo(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.role === 'SUPERADMIN';
  }, []);

  const handleVerificationStart = (lenderId, formData, userId) => {
    if (isSuperAdmin) {
      handleClose();
    } else {
      setVerificationData({ lenderId, userId, formData });
      setShowVerification(true);
    }
  };

  const handleVerificationClose = () => {
    setShowVerification(false);
    setVerificationData({ lenderId: null, userId: null, formData: null });
    
    setTimeout(() => {
      if (onClose) {
        onClose();
      }
    }, 100);
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      <Dialog 
        open={open && !showVerification} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            background: alpha(theme.palette.primary.main, 0.04),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                color: theme.palette.primary.main,
                mr: 2,
                width: 42,
                height: 42,
              }}
            >
              {initialData ? <EditIcon /> : <PersonAddIcon />}
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                component="div"
                fontWeight="bold"
                sx={{
                  color: theme.palette.text.primary,
                  fontFamily: "'Poppins', sans-serif",
                  letterSpacing: "0.02em",
                  fontSize: "1.125rem",
                }}
              >
                {initialData ? 'Edit Lender' : 'Add New Lender'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.8),
                  fontFamily: "'Poppins', sans-serif",
                  display: "block",
                  mt: 0.5,
                }}
              >
                Lender Account
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <LenderOnboardingForm 
            initialData={initialData}
            onVerificationStart={handleVerificationStart}
          />
        </DialogContent>
      </Dialog>

      {!isSuperAdmin && (
        <Dialog 
          open={showVerification} 
          onClose={handleVerificationClose}
          maxWidth="md"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 3,
              background: alpha(theme.palette.primary.main, 0.04),
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                  color: theme.palette.primary.main,
                  mr: 2,
                  width: 42,
                  height: 42,
                }}
              >
                <PersonAddIcon />
              </Avatar>
              <Box>
                <Typography
                  variant="h6"
                  component="div"
                  fontWeight="bold"
                  sx={{
                    color: theme.palette.text.primary,
                    fontFamily: "'Poppins', sans-serif",
                    letterSpacing: "0.02em",
                    fontSize: "1.125rem",
                  }}
                >
                  Organization Verification
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(theme.palette.text.secondary, 0.8),
                    fontFamily: "'Poppins', sans-serif",
                    display: "block",
                    mt: 0.5,
                  }}
                >
                  Verify your organization details
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <OrganizationVerification 
              formData={verificationData.formData}
              initialData={initialData}
              lenderId={verificationData.lenderId}
              userId={verificationData.userId}
              preventClose={true}
              onVerificationSuccess={handleVerificationClose}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default LenderOnboardingPopup; 